/* eslint-disable i18next/no-literal-string */
import React, { useState, useEffect } from 'react';
import { Container, Typography, Card, CardContent, Button, Chip, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import * as XLSX from 'xlsx';
import { useStaff } from '../../staff/hooks/useStaff';
import { useRoles } from '../../roles/hooks/useRoles';
import { generateRandomPassword } from '../../../utils/random';
import { FileDropZone } from '../../files/upload';

const VALID_ROLES = [
  'Tandlæge',
  'Tandplejer',
  'Klinikassistent',
  'Elev',
  'Receptionist',
  'Administrator',
  'Andet'
];


export default function StaffParserPage() {
  const [rows, setRows] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [createdStaff, setCreatedStaff] = useState([]);
  const [importing, setImporting] = useState(false);
  const { staff, fetchDepartmentStaff, createStaff, assignRole } = useStaff();
  const { roles, fetchDepartmentRoles } = useRoles();

  useEffect(() => {
    const getData = async () => {
      await fetchDepartmentStaff({ include_inactive: true });
      await fetchDepartmentRoles();
    }
    getData();
  }, [])

  // The sheet comes from the picker or is dropped on the card
  const handleFileUpload = async (event) => readSheet(event.target.files[0]);

  const readSheet = async (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      const existingNames = staff.map(s => s.name.toLowerCase());
      
      const nameCounts = {};
      json.slice(1).forEach(row => {
        const name = row[0]?.toString().toLowerCase() || '';
        if (name) {
          nameCounts[name] = (nameCounts[name] || 0) + 1;
        }
      });
      
      const processedRows = json.slice(1).map((row, idx) => {
        const name = row[0]?.toString() || '';
        const role = row[1]?.toString() || '';
        
        const exclusionReasons = [];
        if (existingNames.includes(name.toLowerCase())) {
          exclusionReasons.push('Already exists');
        }
        if (nameCounts[name.toLowerCase()] > 1) {
          exclusionReasons.push('Duplicate in file');
        }
        if (role && !VALID_ROLES.includes(role)) {
          exclusionReasons.push('Invalid role');
        }
        
        // Check if role exists in department roles
        const roleExists = role ? roles.some(r => r.name === role) : true;
        if (role && !roleExists) {
          exclusionReasons.push('Role not found');
        }
        
        return {
          id: idx,
          name,
          role,
          status: exclusionReasons.length === 0 ? 'Valid' : exclusionReasons.join(', '),
          isValid: exclusionReasons.length === 0
        };
      });
      
      setRows(processedRows);
      const validIds = new Set(processedRows.filter(r => r.isValid).map(r => r.id));
      setRowSelectionModel({ type: 'include', ids: validIds });
      setCreatedStaff([]); // Reset created staff when new file uploaded
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    setImporting(true);
    const selectedIds = Array.from(rowSelectionModel.ids);
    const selectedData = rows.filter(r => selectedIds.includes(r.id));
    
    const results = [];
    const characters = "0123456789";
    
    for (const staffMember of selectedData) {
      const password = generateRandomPassword(8, characters);
      
      const result = await createStaff({
        name: staffMember.name,
        password: password
      });
      
      if (result.success) {
        // Find and assign role if specified
        if (staffMember.role) {
          const matchedRole = roles.find(r => r.name === staffMember.role);
          if (matchedRole) {
            await assignRole(result.data.staff_member_id, matchedRole.role_id);
          }
        }
        
        results.push({
          name: staffMember.name,
          password: password,
          role: staffMember.role,
          success: true
        });
      }
    }
    
    setCreatedStaff(results);
    setImporting(false);
  };

  const handleProcessRowUpdate = (newRow) => {
    const existingNames = staff.map(s => s.name.toLowerCase());
    const exclusionReasons = [];
    
    if (existingNames.includes(newRow.name.toLowerCase())) {
      exclusionReasons.push('Already exists');
    }
    if (newRow.role && !VALID_ROLES.includes(newRow.role)) {
      exclusionReasons.push('Invalid role');
    }
    
    // Check if role exists in department roles
    const roleExists = newRow.role ? roles.some(r => r.name === newRow.role) : true;
    if (newRow.role && !roleExists) {
      exclusionReasons.push('Role not found');
    }
    
    const updatedRow = {
      ...newRow,
      status: exclusionReasons.length === 0 ? 'Valid' : exclusionReasons.join(', '),
      isValid: exclusionReasons.length === 0
    };
    
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    
    if (!updatedRow.isValid) {
      const newIds = new Set(rowSelectionModel.ids);
      newIds.delete(updatedRow.id);
      setRowSelectionModel({ type: 'include', ids: newIds });
    }
    
    return updatedRow;
  };

  const columns = [
    { 
      field: 'name', 
      headerName: 'NAVN', 
      flex: 1, 
      editable: true,
      cellClassName: (params) => {
        if (!params.row.isValid && (params.row.status.includes("Already exists") || params.row.status.includes("Duplicate in file"))) {
          return "error-cell";
        }
        return "";
      }
    },
    { 
      field: 'role', 
      headerName: 'ROLLE', 
      flex: 1, 
      editable: true,
      type: 'singleSelect',
      valueOptions: VALID_ROLES,
      cellClassName: (params) => {
        if (!params.row.isValid && (params.row.status.includes("Invalid role") || params.row.status.includes("Role not found"))) {
          return "error-cell";
        }
        return "";
      }
    },
    {
      field: 'status',
      headerName: 'STATUS',
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.row.isValid ? 'success' : params.row.status.includes('Role not found') ? 'error' : 'warning'}
          size="small"
        />
      )
    }
  ];

  const validCount = rows.filter(r => r.isValid).length;
  const invalidCount = rows.length - validCount;
  const selectedCount = rowSelectionModel.ids.size;

  return (
    <Container maxWidth="lg" sx={{ mt: 6 }}>
      <Card>
        <FileDropZone onFiles={([file]) => readSheet(file)} multiple={false}>
        <CardContent>
          <Box display="flex">
            <Button variant="contained" component="label">
              Upload CSV or ODS
              <input type="file" hidden onChange={handleFileUpload} accept=".csv,.ods" />
            </Button>

            <Box flex={1} />
              
            {rows.length > 0 && (
              <Button 
                variant="contained" 
                color="primary" 
                disabled={selectedCount === 0 || importing}
                onClick={handleImport}
              >
                {importing ? 'Importing...' : `Import Selected (${selectedCount})`}
              </Button>
            )}
          </Box>
          
          {rows.length > 0 && (
            <>
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total: {rows.length} | Valid: {validCount} | Invalid: {invalidCount} | Selected: {selectedCount}
                </Typography>
              </Box>
              
              <Box 
                sx={{
                  height: 600,
                  width: '100%',
                  '& .error-cell': {
                    backgroundColor: "error.light",
                  },
                  '& .MuiDataGrid-root': {
                    border: 'none',
                    '& .MuiDataGrid-main': {
                      '& .MuiDataGrid-columnHeaders': {
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                      },
                      '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        '&:focus': {
                          outline: 'none',
                        },
                      },
                      '& .MuiDataGrid-row': {
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      },
                    },
                  },
                }}
              >
                <DataGrid
                  rows={rows}
                  columns={columns}
                  checkboxSelection
                  disableRowSelectionOnClick
                  rowSelectionModel={rowSelectionModel}
                  onRowSelectionModelChange={(newModel) => {
                    setRowSelectionModel(newModel);
                  }}
                  processRowUpdate={handleProcessRowUpdate}
                  isRowSelectable={(params) => params.row.isValid}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 100 } },
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                />
              </Box>
            </>
          )}

          {createdStaff.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Successfully created {createdStaff.length} staff member(s)
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Created Staff Members and Passwords
              </Typography>
              
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Password</strong></TableCell>
                      <TableCell><strong>Role</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {createdStaff.map((staff, index) => (
                      <TableRow key={index}>
                        <TableCell>{staff.name}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{staff.password}</TableCell>
                        <TableCell>{staff.role || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
        </FileDropZone>
      </Card>
    </Container>
  );
}